import React, { useEffect, useRef } from "react";
import { Chart as ChartJS } from "chart.js/auto";
import { z } from "zod";
import { CommonSchemas } from "@a2ui/web_core/v0_9";
import { createComponentImplementation } from "@a2ui/react/v0_9";

// TrendChart and BreakdownChart both follow the raw Chart.js/auto wrapper
// pattern already used by src/components/DashboardChart.js (create the
// chart in a useEffect keyed on the resolved props, destroy it on cleanup)
// rather than introducing react-chartjs-2 as a second charting approach.

// ---- TrendChart: a line chart over time -----------------------------------

export const TrendChartApi = {
  name: "TrendChart",
  schema: z.object({
    title: CommonSchemas.DynamicString.optional(),
    xLabel: CommonSchemas.DynamicString.optional(),
    yLabel: CommonSchemas.DynamicString.optional(),
    seriesLabel: CommonSchemas.DynamicString.optional(),
    // Bound via {path: "/..."} to an array of {x, y} (or {label, value})
    // objects already sitting in the surface's data model. The shape of
    // each point is intentionally left to the backend rather than modeled
    // field-by-field in the schema.
    points: CommonSchemas.DynamicValue,
  }),
};

export const TrendChart = createComponentImplementation(TrendChartApi, ({ props }) => {
  const canvasRef = useRef(null);
  const points = Array.isArray(props.points) ? props.points : [];
  const { title, xLabel, yLabel, seriesLabel } = props;

  useEffect(() => {
    if (!canvasRef.current) return undefined;

    const chart = new ChartJS(canvasRef.current, {
      type: "line",
      data: {
        labels: points.map((p) => p?.x ?? p?.label ?? ""),
        datasets: [
          {
            label: seriesLabel || "Value",
            data: points.map((p) => p?.y ?? p?.value ?? 0),
            borderColor: "rgba(54, 162, 235, 1)",
            backgroundColor: "rgba(54, 162, 235, 0.2)",
            tension: 0.3,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          title: { display: !!title, text: title },
        },
        scales: {
          x: { title: { display: !!xLabel, text: xLabel } },
          y: { title: { display: !!yLabel, text: yLabel } },
        },
      },
    });

    return () => chart.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(points), title, xLabel, yLabel, seriesLabel]);

  return (
    <div className="analytics-trend-chart" style={{ position: "relative", height: 260 }}>
      <canvas ref={canvasRef} />
    </div>
  );
});

// ---- BreakdownChart: bar or doughnut broken down by category --------------

export const BreakdownChartApi = {
  name: "BreakdownChart",
  schema: z.object({
    title: CommonSchemas.DynamicString.optional(),
    // "bar" | "doughnut" - defaults to "bar" for anything else.
    chartType: CommonSchemas.DynamicString.optional(),
    // Array of {label, value} objects, e.g. counts by city or device type.
    items: CommonSchemas.DynamicValue,
  }),
};

const PALETTE = [
  "rgba(255, 99, 132, 0.6)",
  "rgba(54, 162, 235, 0.6)",
  "rgba(255, 206, 86, 0.6)",
  "rgba(75, 192, 192, 0.6)",
  "rgba(153, 102, 255, 0.6)",
  "rgba(255, 159, 64, 0.6)",
];

export const BreakdownChart = createComponentImplementation(BreakdownChartApi, ({ props }) => {
  const canvasRef = useRef(null);
  const items = Array.isArray(props.items) ? props.items : [];
  const chartType = props.chartType === "doughnut" ? "doughnut" : "bar";
  const { title } = props;

  useEffect(() => {
    if (!canvasRef.current) return undefined;

    const chart = new ChartJS(canvasRef.current, {
      type: chartType,
      data: {
        labels: items.map((it) => it?.label ?? it?.category ?? ""),
        datasets: [
          {
            label: title || "Breakdown",
            data: items.map((it) => it?.value ?? 0),
            backgroundColor: items.map((_, i) => PALETTE[i % PALETTE.length]),
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: chartType === "doughnut", position: "bottom" },
          title: { display: !!title, text: title },
        },
        ...(chartType === "bar" && {
          scales: {
            y: { beginAtZero: true },
          },
        }),
      },
    });

    return () => chart.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(items), chartType, title]);

  return (
    <div className="analytics-breakdown-chart" style={{ position: "relative", height: 260 }}>
      <canvas ref={canvasRef} />
    </div>
  );
});
